/**
 * The blackjack table — the third composition of `parts.tsx`.
 *
 * ============================== WHAT THIS IS ===================================
 * `routes/Blackjack.tsx` drew its own felt: a CSS half-moon, its own card rows, its own
 * chips. This is the half-moon PLATE from the art kit (`blackjack-topdown-2200.webp`)
 * with the kit's pieces laid on it, the way `TopDownTable` and `SaloonTable` lay the
 * same pieces on the poker plate: `Portrait`, `TimerRing`, `SeatTag`/`StackNum`, the
 * `ChipStacks` piles, `PlayingCard` in its compact face, `TopBar`, and the flight layer
 * (`FlightLayer` with a blackjack planner — `blackjack-flight.ts`). Nothing here is a
 * second card, a second chip or a second timer; what is blackjack's own is WHERE each
 * piece goes (`lib/blackjack-geometry.ts`, ported from the two mocks) and the three
 * things only this table has: a dealer, a shoe and tray, and the printed cloth.
 *
 * It renders from a `BjView` — the table as PRESENTED so far (`blackjack-view.ts`) —
 * and from nothing that could reach into the engine. The route builds the action band
 * and hands it in, exactly as poker's route does.
 *
 * ## Orientation
 *
 * The half-moon never rotates. Landscape and desktop fit the whole moon with the mock's
 * pads; a portrait phone draws it at 155% of the stage's width, anchored under the bar,
 * so the arc's ends run off both sides and the spots stay big — the mock's rule, in
 * `blackjack.css` as a container query. Every placement is emitted for BOTH
 * orientations (`--x`/`--y` and `--xp`/`--yp`) and CSS picks, the way the top-down poker
 * table does it: nothing here listens to a resize.
 * ===============================================================================
 */
import type { ReactNode } from "react";
import type { TableState } from "@engine/table-state";
import { PlayingCard } from "../components/PlayingCard";
import { ChipStacks } from "./ChipStacks";
import { Portrait, TimerRing, SeatTag, TopBar, motionVars, DISC, type SeatTimer, type WalletView } from "./parts";
import { FlightLayer, type FlightBus } from "./flight";
import type { BjDurations } from "./pace";
import type { BjStep } from "./blackjack-director";
import { bjFlights } from "./blackjack-flight";
import {
  OUTCOME_WORD, isBust, naturalView, totalLabel, totalShort, type BjView, type BjHandView,
} from "./blackjack-view";
import {
  PLATE_ASPECT, DEALER_AT, DEALER_HAND, SHOE_AT, TRAY_AT, HOUSE_AT, SLOT,
  seatGeometry, youDisc,
} from "../lib/blackjack-geometry";
import { BlackjackPrint } from "./BlackjackPrint";
import { standing, type StandingCrop } from "../art/casting";
import { useMoney } from "../lib/MoneyContext";
import type { BlackjackRules } from "@engine/blackjack/rules";
import "./topdown.css";
import "./blackjack.css";

/** The plate `sync-art.mjs` ships: the 2200 tier is the only one there is (the 1100 and 800 are owed). */
export const BJ_PLATE = "/art/table/blackjack-topdown-2200.webp";

export type ShoeState = "none" | "shuffling" | "full";

export interface BlackjackTableProps {
  view: BjView | null;
  rules: BlackjackRules;
  /** Short names, by seat. The hero's is whatever the room calls the player. */
  names: readonly string[];
  /** Roster slugs for the seats that are not the hero — the sitting's casting (`casting.ts`). */
  faces: Record<number, string>;
  /** The player's chosen face; null draws the silhouette. */
  heroAvatar: string | null;
  /** The player's stack, in Bucks. Only the player has one — see the room. */
  heroStack: number;
  dealer: { name: string; slug: string };
  /**
   * Stand the dealer up behind the moon rather than seat them as a disc on their plate.
   * The route passes the saloon's own `saloonFits` rule (width >= 1024, aspect >= 1.3);
   * the harness passes it per frame. A crop the keyer could not do cleanly never stands
   * — the disc it is, as in the saloon.
   */
  standingDealer?: boolean;
  timers?: Record<number, SeatTimer>;
  plate?: string;
  plateAspect?: number;
  /** The shoe's and the tray's art, when Tim's render lands. Absent, both are drawn in CSS. */
  shoeArt?: string | null;
  trayArt?: string | null;
  shoe?: ShoeState;
  /** Cards lying in the discard tray, between the collect and the next shuffle. */
  tray?: number;
  actions?: ReactNode;
  chrome?: "room" | "own";
  context?: string;
  wallet?: WalletView | null;
  onSettings?: () => void;
  flights?: FlightBus<BjStep, BjView>;
  motion?: BjDurations;
}

const pct = (v: number) => `${(v * 100).toFixed(3)}%`;
const at = (p: readonly [number, number]) => ({ "--x": pct(p[0]), "--y": pct(p[1]) } as React.CSSProperties);

/** A hand's cards as they lie on the cloth: each next card down-and-right of the last. */
function CardRow({ cards, kind, hole, className, dataTd, seat, hand }: {
  cards: readonly (number | null)[]; kind: "bj" | "bj-hero";
  /** The dealer's hand: the second card turns over when it becomes a number. */
  hole?: boolean; className: string; dataTd: string; seat?: number; hand?: number;
}) {
  return (
    <span className={className} data-td={dataTd} data-seat={seat} data-hand={hand}
          style={{ "--n": cards.length } as React.CSSProperties}>
      {cards.map((c, i) => (
        <span key={i} className="bk-cardslot" style={{ "--i": i } as React.CSSProperties}>
          {hole && i === 1 ? (
            /* The hole card: a back until it is turned, then the same 3-D flip the
               board uses — so there is one flip in the product, not two. */
            c === null
              ? <PlayingCard kind={kind} hidden />
              : (
                <span className="td-flip3d">
                  <span className="td-flip3d-back"><PlayingCard kind={kind} hidden /></span>
                  <span className="td-flip3d-face"><PlayingCard kind={kind} card={c} /></span>
                </span>
              )
          ) : c === null ? <PlayingCard kind={kind} hidden /> : <PlayingCard kind={kind} card={c} />}
        </span>
      ))}
    </span>
  );
}

/** The up-card as a table names it: "A", "10", "6". */
function upLabel(card: number): string {
  const r = card >> 2;
  return r === 12 ? "A" : r >= 8 ? "10" : String(r + 2);
}

/** A hand's result, for the pill beside it — through `OUTCOME_WORD`, never the engine's own word. */
function handNote(h: BjHandView): string {
  if (!h.cards.length) return "";
  if (h.outcome && !isBust(h.cards)) return `${totalLabel(h.cards)} · ${OUTCOME_WORD[h.outcome]}`;
  return totalLabel(h.cards);
}

export function BlackjackTable({
  view, rules, names, faces, heroAvatar, heroStack, dealer, standingDealer = false, timers,
  plate = BJ_PLATE, plateAspect = PLATE_ASPECT, shoeArt = null, trayArt = null,
  shoe = "full", tray = 0, actions, chrome = "own", context, wallet, onSettings, flights, motion,
}: BlackjackTableProps) {
  const { say } = useMoney();
  const n = view?.seats.length ?? names.length;
  const geo = seatGeometry(n === 1 || n === 3 || n === 5 ? n : 3);
  const crop: StandingCrop | null = standingDealer ? standing(dealer.slug, "three-quarter") : null;
  const hero = view?.seats.find((s) => s.isHero) ?? null;
  const dealerCards = view?.dealer ?? [];
  const dealerOpen = dealerCards.length > 1 && dealerCards[1] !== null;
  /* Before the hole card turns, the dealer SHOWS a card, and a table says "shows an ace",
     not "shows soft eleven". */
  const dealerNumber = !dealerCards.length ? "" : dealerOpen ? totalShort(dealerCards) : upLabel(dealerCards[0]!);
  const turn = view?.turn ?? null;

  /* The bar's shape of a table, for the one child that is not blackjack-specific. */
  const barState = {
    seats: names.map((shortName) => ({ identity: { shortName } })),
    handNumber: view?.handNumber ?? 0,
  };

  return (
    <div className="td-frame" style={{ "--plate-asp": plateAspect } as React.CSSProperties}>
      <div className={"td-app bk-app" + (crop ? " bk-standing" : "")} data-seats={n} style={motionVars(motion)}>
        {chrome === "own" && (
          <TopBar state={barState} context={context ?? `Blackjack · ${rules.decks} decks · ${rules.dealerStandsSoft17 ? "S17" : "H17"}`}
                  wallet={wallet} onSettings={onSettings} />
        )}

        <div className="td-stage bk-stage">
          <div className="bk-feltfit">
            <div className="bk-felt" data-td="felt">
              {/* The dealer stands BEHIND the moon: drawn before the plate, cut at the edge. */}
              {crop && (
                <span className="bk-dealer-figure" style={{ "--crop-asp": crop.aspect } as React.CSSProperties}>
                  <img src={crop.url} alt="" aria-hidden="true"
                       className={crop.facing === "r" ? "bk-face-r" : "bk-face-l"} />
                  <span className="bk-dealer-face" data-td="face" aria-hidden="true"
                        style={{
                          left: pct(crop.facing === "r" ? crop.face.x : 1 - crop.face.x - crop.face.w),
                          top: pct(crop.face.y), width: pct(crop.face.w), height: pct(crop.face.h),
                        }} />
                </span>
              )}
              <img className="bk-plate" src={plate} alt="" aria-hidden="true" />
              <BlackjackPrint rules={rules} seats={n} />

              {/* The dealer's plate: the disc (or, standing, just the plate) and the count. */}
              <span className={"bk-dealer" + (crop ? " bk-dealer-standing" : "")} style={at(DEALER_AT)}
                    data-td="dealer">
                {!crop && (
                  <span className="td-av bk-dealer-av" data-td="av">
                    <Portrait name={dealer.name} slug={dealer.slug} className="td-av-img" />
                  </span>
                )}
                <span className="td-tag bk-dealer-tag"
                      aria-label={`${dealer.name}, the dealer${dealerNumber ? `, ${dealerOpen ? "has" : "shows"} ${dealerNumber}` : ""}`}>
                  <b>{dealer.name}</b>
                  <span className="num bk-dealer-num">
                    {dealerNumber ? <>{dealerOpen ? "" : <em>shows </em>}{dealerNumber}</> : <em>dealer</em>}
                  </span>
                </span>
              </span>

              {/* The house's chips live in the brass tray the plate carries; payouts come from it. */}
              <span className="bk-house" data-td="house" style={at(HOUSE_AT)} aria-hidden="true" />

              {/* The shoe and the discard tray: slots, with art when it lands. */}
              <span className={"bk-slot bk-shoe" + (shoe === "shuffling" ? " bk-shuffling" : "")}
                    data-td="deck" aria-hidden="true"
                    style={{ ...at(SHOE_AT), "--slot-w": pct(SLOT.w), "--slot-h": `${(SLOT.h * plateAspect * 100).toFixed(3)}%` } as React.CSSProperties}>
                {shoeArt ? <img className="bk-slot-art" src={shoeArt} alt="" /> : <span className="bk-slot-body" />}
                {shoe !== "none" && (
                  <span className="bk-shoe-cards">
                    {[0, 1, 2].map((k) => (
                      <span key={k} className="bk-shoe-card" style={{ "--k": k } as React.CSSProperties}>
                        <PlayingCard kind="bj" hidden />
                      </span>
                    ))}
                  </span>
                )}
              </span>
              <span className="bk-slot bk-tray" data-td="discard" aria-hidden="true"
                    style={{ ...at(TRAY_AT), "--slot-w": pct(SLOT.w), "--slot-h": `${(SLOT.h * plateAspect * 100).toFixed(3)}%` } as React.CSSProperties}>
                {trayArt ? <img className="bk-slot-art" src={trayArt} alt="" /> : <span className="bk-slot-body" />}
                {tray > 0 && (
                  <span className="bk-tray-cards">
                    {Array.from({ length: Math.min(4, Math.ceil(tray / 4)) }, (_, k) => (
                      <span key={k} className="bk-shoe-card" style={{ "--k": k } as React.CSSProperties}>
                        <PlayingCard kind="bj" hidden />
                      </span>
                    ))}
                  </span>
                )}
              </span>

              {/* The dealer's hand, just under the tray. */}
              <span className="bk-dhand-at" style={at(DEALER_HAND)}>
                <CardRow cards={dealerCards} kind="bj" hole className="bk-hand bk-dhand" dataTd="dealerhand" />
              </span>

              {/* ---------------------------------------------------- the spots --- */}
              {geo.map((g) => {
                const s = view?.seats[g.seat];
                const hands = s?.hands ?? [];
                const insurance = s?.insurance ?? 0;
                const mine = !!s?.isHero;
                return (
                  <span key={`spot${g.seat}`} className={"bk-spot" + (mine ? " bk-spot-hero" : "")}
                        style={at(g.spot)} data-seat={g.seat}>
                    {/* The chips in the circle — one pile per hand, what is actually at risk. */}
                    <span className="bk-circle-chips">
                      {(hands.length ? hands : [null]).map((h, hi) => (
                        <span key={hi} className={"td-bet bk-bet" + (h && h.bet > 0 ? "" : " td-bet-empty")}
                              data-td="bet" data-seat={g.seat} data-hand={hi}
                              aria-label={h && h.bet > 0 ? `${names[g.seat]} has ${say(h.bet * 25)} in the circle` : undefined}>
                          {h && h.bet > 0 ? <ChipStacks key={h.bet} amount={h.bet * 25} /> : <span className="td-chips" />}
                        </span>
                      ))}
                    </span>
                    {/* A bot's cards, above its circle. The player's are big, in the hero zone. */}
                    {!mine && hands.length > 0 && (
                      /* The hand grows toward the middle of the table: a seat left of the
                         player lays its cards rightward from its circle's left edge, a seat
                         to the right leftward from its right edge — so the ends of the moon
                         never push a card off a phone. */
                      <span className={"bk-hands" + (hands.length > 1 ? " bk-split" : "") + (g.deg > 90 ? " bk-hands-l" : " bk-hands-r")}
                            data-td="bjhands" data-seat={g.seat}>
                        {hands.map((h, hi) => (
                          <span key={hi} className={"bk-handwrap" + (turn?.seat === g.seat && turn.hand === hi ? " bk-turn" : "")
                                                        + (h.outcome ? ` bk-out-${h.outcome}` : "")}>
                            <CardRow cards={h.cards} kind="bj" className="bk-hand" dataTd="bjhand" seat={g.seat} hand={hi} />
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                );
              })}

              {/* Insurance bets, in front of their own spots. */}
              {geo.map((g) => {
                const ins = view?.seats[g.seat]?.insurance ?? 0;
                return (
                  <span key={`ins${g.seat}`} className={"td-bet bk-ins" + (ins > 0 ? "" : " td-bet-empty")}
                        data-td="ins" data-seat={g.seat} style={at(g.insurance)}
                        aria-label={ins > 0 ? `${names[g.seat]} took insurance` : undefined}>
                    {ins > 0 ? <ChipStacks key={ins} amount={ins * 25} /> : <span className="td-chips" />}
                  </span>
                );
              })}

              {/* The YOU disc, beside the player's own circle. */}
              <span className="bk-you" data-td="you" style={at(youDisc())} aria-hidden="true">
                <img src={DISC} alt="" />
                <span>YOU</span>
              </span>

              {/* ----------------------------------------------------- the seats --- */}
              {geo.map((g) => {
                const s = view?.seats[g.seat];
                const mine = g.seat === 0;
                const hands = s?.hands ?? [];
                const acting = turn?.seat === g.seat;
                const won = hands.some((h) => h.outcome === "win" || h.outcome === "blackjack");
                const lost = hands.length > 0 && hands.every((h) => h.outcome === "lose");
                const shown = hands.filter((h) => h.cards.length);
                const totals = shown.map((h) => (isBust(h.cards) ? `${totalShort(h.cards)} bust` : totalShort(h.cards, naturalView(h)))).join(", ");
                const style = {
                  "--x": pct(g.land.at[0]), "--y": pct(g.land.at[1]), "--ux": g.land.ux, "--uy": g.land.uy,
                  "--xp": pct(g.port.at[0]), "--yp": pct(g.port.at[1]), "--uxp": g.port.ux, "--uyp": g.port.uy,
                } as React.CSSProperties;
                const seatLike = {
                  identity: { name: names[g.seat] ?? "", shortName: names[g.seat] ?? "", avatarId: null },
                  stack: heroStack, allIn: false,
                } as unknown as TableState["seats"][number];
                return (
                  <span key={`seat${g.seat}`}
                        className={"bk-seat td-seat" + (mine ? " td-seat-hero" : "") + (acting ? " td-seat-acting" : "")
                                   + (won ? " td-seat-winner" : "") + (lost ? " td-seat-loser" : "")}
                        data-td="seat" data-seat={g.seat} style={style}>
                    <span className="td-av" data-td="av">
                      <Portrait name={names[g.seat] ?? ""} slug={mine ? undefined : faces[g.seat]}
                                override={mine ? heroAvatar : undefined} className="td-av-img" />
                      <TimerRing timer={timers?.[g.seat]} />
                    </span>
                    {mine ? (
                      /* The player's plate: the stack, the biggest thing on it (Phase 5 0(c)). */
                      <SeatTag s={seatLike} motion={motion} />
                    ) : (
                      /* A seat at this table has no stack — the other chairs are practice
                         opponents, and a bankroll beside them would be a number that means
                         nothing. What a player reads off them is the TOTAL, so the total is
                         the number, at the stack's size. */
                      <span className="td-tag" aria-label={`${names[g.seat]}${totals ? `, ${totals}` : ""}`}>
                        <b>{names[g.seat]}</b>
                        <span className="num td-stack bk-total">
                          {shown.length ? shown.map((h, k) => (
                            /* A split seat shows both totals, one above the other so the plate
                               does not widen into its neighbour's; a hand over 21 is struck
                               through rather than spelled out. */
                            <span key={k} className={"bk-total-hand" + (isBust(h.cards) ? " bk-bust" : "")}>
                              {isBust(h.cards) ? totalShort(h.cards) : totalShort(h.cards, naturalView(h))}
                            </span>
                          )) : "—"}
                        </span>
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* ------------------------------------------------------- hero zone --- */}
        <div className="td-herozone bk-herozone">
          {hero && hero.hands.map((h, hi) => (
            <span key={hi} className={"bk-herohand" + (turn?.seat === hero.index && turn.hand === hi ? " bk-turn" : "")
                                      + (h.outcome ? ` bk-out-${h.outcome}` : "")
                                      + (hero.hands.length > 1 ? " bk-herohand-split" : "")}>
              <CardRow cards={h.cards} kind="bj-hero" className="bk-hand" dataTd="hand" seat={hero.index} hand={hi} />
              {h.cards.length > 0 && <span className="bk-heronote num">{handNote(h)}</span>}
            </span>
          ))}
        </div>

        <div className="td-actions">{actions}</div>

        {/* The felt, spoken. */}
        <p className="sr-only" role="status">
          {dealerNumber ? `${dealer.name} ${dealerOpen ? "has" : "shows"} ${dealerNumber}.` : ""}{" "}
          {hero?.hands.map((h) => handNote(h)).filter(Boolean).join("; ")}
        </p>

        {flights && motion && <FlightLayer bus={flights} durations={motion} plan={bjFlights} />}
      </div>
    </div>
  );
}
